import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Lock, Plus, Scale, Trash2 } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';

const MATERIALS = [
    { id: 1, name: 'Cemento Portland 42.5kg', category: 'Cemento', provider: 'Ferreteria Central', unit: 'bolsa', price: 8.95, updatedDays: 4 },
    { id: 2, name: 'Bloque de concreto 15x20x40', category: 'Mamposteria', provider: 'Materiales El Salvador', unit: 'unidad', price: 0.58, updatedDays: 7 },
    { id: 3, name: 'Varilla 3/8 (6m)', category: 'Acero', provider: 'Hierros SV', unit: 'pieza', price: 7.2, updatedDays: 3 },
    { id: 4, name: 'Arena lavada (m3)', category: 'Agregados', provider: 'Agregados La Sabana', unit: 'm3', price: 28.5, updatedDays: 5 },
    { id: 5, name: 'Grava triturada (m3)', category: 'Agregados', provider: 'Agregados La Sabana', unit: 'm3', price: 30.0, updatedDays: 5 },
    { id: 6, name: 'Ladrillo rojo artesanal', category: 'Mamposteria', provider: 'Ceramicas San Miguel', unit: 'unidad', price: 0.42, updatedDays: 9 },
    { id: 7, name: 'Tubo PVC 1/2"', category: 'Instalaciones', provider: 'Ferreteria Central', unit: 'unidad', price: 0.9, updatedDays: 6 },
    { id: 8, name: 'Codo PVC 1/2"', category: 'Instalaciones', provider: 'Ferreteria Central', unit: 'unidad', price: 0.35, updatedDays: 6 },
    { id: 9, name: 'Ceramica piso 60x60', category: 'Acabados', provider: 'Acabados Premium', unit: 'm2', price: 9.5, updatedDays: 8 },
    { id: 10, name: 'Pintura latex interior (galon)', category: 'Acabados', provider: 'Pinturas Futura', unit: 'galon', price: 19.9, updatedDays: 2 }
];

const ConstructionPlanner = () => {
    const { subscription } = useSubscription();
    const planId = (subscription?.plan || 'esencial').toLowerCase();
    const isPremium = planId === 'premium';

    const [query, setQuery] = useState('');
    const [quantities, setQuantities] = useState({});
    const [budgetItems, setBudgetItems] = useState([]);
    const [compareIds, setCompareIds] = useState([]);
    const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);

    const filteredMaterials = useMemo(() => {
        const term = query.trim().toLowerCase();
        if (!term) return MATERIALS;
        return MATERIALS.filter((item) => {
            return (
                item.name.toLowerCase().includes(term) ||
                item.category.toLowerCase().includes(term) ||
                item.provider.toLowerCase().includes(term)
            );
        });
    }, [query]);

    const addToBudget = (material) => {
        const qty = Math.max(1, Number(quantities[material.id] || 1));
        setBudgetItems((prev) => {
            const existing = prev.find((item) => item.id === material.id);
            if (existing) {
                return prev.map((item) => item.id === material.id ? { ...item, quantity: qty } : item);
            }
            return [...prev, { id: material.id, quantity: qty }];
        });
    };

    const updateBudgetQty = (id, value) => {
        const qty = Math.max(1, Number(value || 1));
        setBudgetItems((prev) => prev.map((item) => item.id === id ? { ...item, quantity: qty } : item));
    };

    const removeBudgetItem = (id) => {
        setBudgetItems((prev) => prev.filter((item) => item.id !== id));
    };

    const toggleCompare = (id) => {
        setCompareIds((prev) => {
            if (prev.includes(id)) return prev.filter((item) => item !== id);
            if (prev.length >= 3) return prev;
            return [...prev, id];
        });
    };

    const budgetLines = budgetItems.map((line) => {
        const material = MATERIALS.find((item) => item.id === line.id);
        return material ? { ...material, quantity: line.quantity } : null;
    }).filter(Boolean);

    const total = budgetLines.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const compareItems = MATERIALS.filter((item) => compareIds.includes(item.id));
    const lowestPrice = compareItems.length ? Math.min(...compareItems.map((item) => item.price)) : null;

    return (
        <div className="space-y-6">
            <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <CardTitle className="text-xl font-bold text-slate-900">Buscador de Materiales</CardTitle>
                            <p className="text-sm text-slate-500">Compara precios reales y arma tu presupuesto sin salir de la plataforma.</p>
                        </div>
                        <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                            Actualizado semanalmente
                        </Badge>
                    </div>
                    <div className="mt-4">
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Busca cemento, bloque, hierro 3/8..."
                            className="h-11 rounded-xl border-slate-200"
                        />
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-3">
                        {filteredMaterials.length === 0 && (
                            <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                <div className="text-slate-400 mb-3">🔍</div>
                                <h4 className="text-slate-900 font-bold">No se encontraron resultados</h4>
                                <p className="text-slate-500 text-sm mt-1 max-w-xs mx-auto">
                                    Intenta revisar la ortografía o usa términos más generales como "cemento" o "bloque".
                                </p>
                            </div>
                        )}
                        {filteredMaterials.map((material) => (
                            <div key={material.id} className="flex flex-col md:flex-row md:items-center gap-4 border border-slate-100 rounded-xl p-4 bg-white">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-semibold text-slate-900">{material.name}</h3>
                                        <Badge variant="outline" className="text-xs">{material.category}</Badge>
                                    </div>
                                    <p className="text-sm text-slate-500">Proveedor: {material.provider}</p>
                                    <p className="text-xs text-slate-400">Precio actualizado hace {material.updatedDays} dias</p>
                                </div>
                                <div className="flex flex-col items-start md:items-end gap-2">
                                    <div className="text-lg font-bold text-slate-900">${material.price.toFixed(2)} <span className="text-xs text-slate-400">/{material.unit}</span></div>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="number"
                                            min="1"
                                            value={quantities[material.id] || 1}
                                            onChange={(e) => setQuantities((prev) => ({ ...prev, [material.id]: e.target.value }))}
                                            className="w-20 h-9"
                                        />
                                        <Button size="sm" className="gap-1" onClick={() => addToBudget(material)}>
                                            <Plus className="w-4 h-4" /> Agregar
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant={compareIds.includes(material.id) ? 'default' : 'outline'}
                                            className={`gap-1 transition-all ${compareIds.includes(material.id) ? 'bg-blue-600 text-white border-blue-600' : ''}`}
                                            onClick={() => toggleCompare(material.id)}
                                        >
                                            <Scale className="w-4 h-4" />
                                            {compareIds.includes(material.id) ? '✓ En comparación' : 'Comparar'}
                                            {compareIds.length > 0 && !compareIds.includes(material.id) && (
                                                <span className="text-[10px] ml-1 opacity-60">({compareIds.length}/3)</span>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {compareItems.length > 0 && (
                <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg font-bold text-slate-900">Comparador de precios</CardTitle>
                        <p className="text-sm text-slate-500">Selecciona hasta 3 materiales para comparar.</p>
                    </CardHeader>
                    <CardContent className="grid md:grid-cols-3 gap-4">
                        {compareItems.map((item) => (
                            <div key={item.id} className="border border-slate-100 rounded-xl p-4 bg-slate-50">
                                <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-slate-900">{item.name}</h4>
                                    {item.price === lowestPrice && (
                                        <Badge className="bg-emerald-500 text-white">Mejor precio</Badge>
                                    )}
                                </div>
                                <p className="text-sm text-slate-500 mt-2">Proveedor: {item.provider}</p>
                                <p className="text-sm text-slate-500">Unidad: {item.unit}</p>
                                <p className="text-lg font-bold text-slate-900 mt-3">${item.price.toFixed(2)}</p>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <CardTitle className="text-lg font-bold text-slate-900">Presupuesto editable</CardTitle>
                        <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                                {budgetLines.length} materiales
                            </Badge>
                            <Badge className="bg-blue-600 text-white">
                                Total ${total.toFixed(2)}
                            </Badge>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {budgetLines.length === 0 && (
                        <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center text-slate-500">
                            Agrega materiales desde el buscador para armar tu presupuesto.
                        </div>
                    )}
                    {budgetLines.map((item) => (
                        <div key={item.id} className="flex flex-col md:flex-row md:items-center gap-4 border border-slate-100 rounded-xl p-4 bg-white">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-slate-900">{item.name}</h4>
                                    <Badge variant="outline" className="text-xs">{item.category}</Badge>
                                </div>
                                <p className="text-sm text-slate-500">Proveedor: {item.provider}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <Input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => updateBudgetQty(item.id, e.target.value)}
                                    className="w-24 h-9"
                                />
                                <div className="text-sm text-slate-500">x ${item.price.toFixed(2)}</div>
                                <div className="text-base font-semibold text-slate-900">${(item.price * item.quantity).toFixed(2)}</div>
                                <Button variant="ghost" size="icon" onClick={() => removeBudgetItem(item.id)}>
                                    <Trash2 className="w-4 h-4 text-slate-500" />
                                </Button>
                            </div>
                        </div>
                    ))}
                    <div className="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-emerald-100 rounded-xl">
                                <CheckCircle className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 leading-tight">Presupuesto Listo</h4>
                                <p className="text-sm text-slate-500">Puedes presentar este desglose profesional a tus prospectos.</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <div className="flex flex-col items-center gap-1 w-full md:w-auto overflow-visible group/export">
                                <Button
                                    onClick={() => {
                                        if (!isPremium) {
                                            setIsPremiumModalOpen(true);
                                            return;
                                        }
                                        alert("Generando PDF Premium...");
                                    }}
                                    size="lg"
                                    className={`w-full md:flex-none h-14 px-8 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl relative ${isPremium ? 'bg-[#1A3D5C] hover:bg-[#162e46] text-white' : 'bg-slate-100 text-slate-400 border border-slate-200'}`}
                                >
                                    {!isPremium && <Lock className="w-4 h-4 mr-2" />}
                                    Exportar Reporte PDF
                                    {!isPremium && (
                                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-amber-600 text-white text-[10px] px-3 py-1.5 rounded-lg opacity-0 group-hover/export:opacity-100 transition-opacity whitespace-nowrap shadow-xl font-bold border border-amber-500 pointer-events-none after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-amber-600">
                                            🌟 Función Premium
                                        </div>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Premium Export Modal */}
                    {isPremiumModalOpen && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
                            <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-100 text-center relative">
                                <button
                                    onClick={() => setIsPremiumModalOpen(false)}
                                    className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors"
                                >
                                    <Plus className="w-6 h-6 rotate-45 text-slate-400" />
                                </button>

                                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Lock className="w-10 h-10 text-amber-600" />
                                </div>

                                <h3 className="text-2xl font-bold text-slate-900 mb-2">📄 Exportación Profesional (Premium)</h3>
                                <p className="text-slate-500 mb-8 leading-relaxed">
                                    La exportación en PDF está disponible únicamente en el Plan Premium.
                                    Obtén un documento profesional listo para compartir con clientes o arquitectos.
                                </p>

                                <div className="space-y-3">
                                    <Button
                                        onClick={() => window.location.assign('/subscription')}
                                        className="w-full h-12 bg-primary text-white font-bold rounded-xl shadow-lg hover:shadow-xl"
                                    >
                                        Activar Plan Premium
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={() => setIsPremiumModalOpen(false)}
                                        className="w-full h-12 text-slate-400 font-medium"
                                    >
                                        Seguir editando
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ConstructionPlanner;
